import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RiLogoutCircleRLine } from "react-icons/ri";
import toast from "react-hot-toast";
import SidebarHeader from "./SidebarHeader";
import SidebarMenuItem from "./SidebarMenuItem";
import { DefaultImageView } from "../../../components/avatar/defaultAvatar";
import { _logoutUser } from "../../../../store/actions/user";
import { getPseudoDeviceId } from "../../../components/sidebar/components/workspaceDropdown";
import { findParentMenuLabelForPath } from "./menuPathUtils";

const COLLAPSED_W = 72;
const EXPANDED_W = 270;

const Sidebar = ({ menu = [], settingmenu = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state?.user?.userInfo);
  const user = userInfo?.user || { name: "User", profile_image: null };

  const [isExpanded, setIsExpanded] = useState(false);
  const [openMenuLabel, setOpenMenuLabel] = useState(null);
  const collapseTimer = useRef(null);

  // Keep the submenu open for the section that matches the current page (e.g. HR Setup → Departments)
  useEffect(() => {
    const path = location.pathname;
    const label =
      findParentMenuLabelForPath(menu, path) ||
      findParentMenuLabelForPath(settingmenu, path);
    setOpenMenuLabel(label);
  }, [location.pathname, menu, settingmenu]);

  const handleMouseEnter = () => {
    clearTimeout(collapseTimer.current);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    collapseTimer.current = setTimeout(() => {
      setIsExpanded(false);
      // Keep openMenuLabel so when the user opens the sidebar again they still see
      // which section (e.g. HR Setup) matches the current route.
    }, 200);
  };

  const handleToggleMenu = (label) => {
    setOpenMenuLabel((prev) => (prev === label ? null : label));
  };

  const handleLogout = () => {
    const device_id = getPseudoDeviceId();
    dispatch(
      _logoutUser({ device_id }, (error) => {
        if (error) {
          return toast.error("Failed to logout!");
        }
        navigate("/auth/login");
      })
    );
  };

  return (
    <>
      {/* Spacer — always takes 72px in the document flow */}
      <div
        className="hidden md:block flex-shrink-0"
        style={{ width: COLLAPSED_W }}
      />

      {/* Actual sidebar — positioned fixed so it overlays content on expand */}
      <aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="hidden md:flex flex-col fixed left-0 top-0 h-screen z-40 overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{ width: isExpanded ? EXPANDED_W : COLLAPSED_W }}
      >
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-gradientStart to-gradientEnd" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-black/[0.08]" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/10 to-transparent" />

        {/* Shadow when expanded */}
        {isExpanded && (
          <div className="absolute inset-y-0 right-0 w-px bg-white/[0.06] shadow-[4px_0_24px_rgba(0,0,0,0.15)]" />
        )}

        {/* Content */}
        <div className="relative flex flex-col h-full z-10">
          {/* Header */}
          <SidebarHeader isExpanded={isExpanded} />

          {/* Separator */}
          <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent" />

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 scrollbar-hidden">
            {menu.map((item, idx) => (
              <SidebarMenuItem
                key={`menu-${idx}`}
                item={item}
                isExpanded={isExpanded}
                openMenuLabel={openMenuLabel}
                onToggleMenu={handleToggleMenu}
              />
            ))}

            {settingmenu.length > 0 && (
              <>
                <div className="mx-1 my-2 h-px bg-gradient-to-r from-transparent via-white/[0.1] to-transparent" />
                {settingmenu.map((item, idx) => (
                  <SidebarMenuItem
                    key={`setting-${idx}`}
                    item={item}
                    isExpanded={isExpanded}
                    openMenuLabel={openMenuLabel}
                    onToggleMenu={handleToggleMenu}
                  />
                ))}
              </>
            )}
          </nav>

          {/* Separator */}
          <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent" />

          {/* User profile */}
          <div className="flex-shrink-0 px-3 py-2.5">
            {isExpanded ? (
              <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl bg-white/[0.07] border border-white/[0.08] hover:bg-white/[0.11] transition-all duration-200">
                <button
                  onClick={() => navigate("/profile")}
                  className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-white/15 hover:ring-white/30 transition-all duration-200"
                >
                  <DefaultImageView
                    image={user?.profile_image}
                    name={user?.name}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[12.5px] font-semibold truncate capitalize leading-tight">
                    {user?.name}
                  </p>
                  <p className="text-white/40 text-[10.5px] truncate mt-0.5 font-medium">
                    {user?.designation?.name || "Employee"}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-white/25 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 flex-shrink-0"
                  title="Logout"
                >
                  <RiLogoutCircleRLine className="w-[13px] h-[13px]" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5">
                <button
                  onClick={() => navigate("/profile")}
                  className="w-9 h-9 rounded-xl overflow-hidden ring-1 ring-white/10 hover:ring-white/25 transition-all duration-200 shadow-lg"
                >
                  <DefaultImageView
                    image={user?.profile_image}
                    name={user?.name}
                    className="!w-full !h-full !rounded-xl"
                  />
                </button>
                <button
                  onClick={handleLogout}
                  className="w-8 h-8 flex items-center justify-center rounded-xl text-white/35 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                  title="Logout"
                >
                  <RiLogoutCircleRLine className="w-[14px] h-[14px]" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
